import React, { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react'; 
import { X, CalendarDays, Newspaper, ShoppingCart, ChevronLeft, ChevronRight, Zap, Gift, Edit, ShieldCheck, PhoneCall, Settings, Maximize2, Calendar, CreditCard } from 'lucide-react';

import itemService from '../../services/itemService';
import catalogService from '../../services/catalogService'; 
import { newsService } from '../../services/contentService';
import { cartService } from '../../services/cartService'; 

import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import { getImageUrl, cleanHtmlContent, generateSlug, fixHtmlImageUrls } from '../../utils/helpers';
import { toast } from 'react-toastify';
import { useCart } from '../../contexts/CartContext';
import { PRODUCT_STATUS, PRODUCT_STATUS_CONFIG } from '../../utils/constants';

import 'react-quill-new/dist/quill.snow.css';

const ProductRow = React.lazy(() => import('../../components/home/HomeProductSection').then(module => ({ default: module.ProductRow })));

// 👉 TỐI ƯU 1: Xử lý LCP & CLS cho các ảnh nội dung sinh ra từ CKEditor/Quill
const SafeHtml = React.memo(React.forwardRef(({ content, className }, ref) => {
    if (!content) return null;
    const cleaned = cleanHtmlContent(content);
    let finalHtml = fixHtmlImageUrls(cleaned);

    let firstImgFound = false;
    finalHtml = finalHtml.replace(/<img\s[^>]*>/gi, (match) => {
        if (!firstImgFound) {
            firstImgFound = true;
            // Ảnh đầu trong bài viết: Cấm lazy load
            if (!match.match(/fetchpriority/i)) {
                return match.replace(/<img\s/i, '<img fetchpriority="high" loading="eager" decoding="async" ');
            }
            return match;
        } else {
            // Các ảnh sau: Ép lazy load để tránh nghẽn LCP
            if (!match.match(/loading/i)) {
                return match.replace(/<img\s/i, '<img loading="lazy" decoding="async" ');
            }
            return match;
        }
    });

    return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: finalHtml }} />;
}));

const ProductDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const WebLogo = '/logo2.png';
    
    const id = slug ? slug.split('-id').pop() : null;
    
    const [product, setProduct] = useState(null);
    const [promotions, setPromotions] = useState([]); 
    const [breadcrumbs, setBreadcrumbs] = useState([]); 
    const [news, setNews] = useState([]); 
    const [loading, setLoading] = useState(true);

    const [sortedImages, setSortedImages] = useState([]); 
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const { fetchCart } = useCart();
    const [purchaseQuantity, setPurchaseQuantity] = useState(1);

    const [timeLeft, setTimeLeft] = useState(null);
    const [isFlashSaleActive, setIsFlashSaleActive] = useState(false);
    const [flashSaleWindow, setFlashSaleWindow] = useState({ start: null, end: null });
    
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isContentLong, setIsContentLong] = useState(false);
    const descriptionRef = useRef(null);
    const sectionRef = useRef(null);

    const [adminSpecs, setAdminSpecs] = useState([]);
    const [productPolicies, setProductPolicies] = useState([]);
    const [isAdminOrStaff, setIsAdminOrStaff] = useState(false);
    const [isSpecModalOpen, setIsSpecModalOpen] = useState(false);

    const [bestSellers, setBestSellers] = useState([]);
    const [similarItems, setSimilarItems] = useState([]);
    const [rentalNote, setRentalNote] = useState("");

    useEffect(() => {
        if (descriptionRef.current) {
            if (descriptionRef.current.scrollHeight > 400) {
                setIsContentLong(true);
            } else {
                setIsContentLong(false);
            }
        }
    }, [product?.description]);

    useEffect(() => {
        if (sortedImages.length <= 1 || isLightboxOpen) return;
        const slideInterval = setInterval(() => {
            setActiveImageIndex((prev) => (prev === sortedImages.length - 1 ? 0 : prev + 1));
        }, 10000);
        return () => clearInterval(slideInterval);
    }, [sortedImages.length, isLightboxOpen]);

    useEffect(() => {
        if (!product || !product.itemFlashSalePrice || product.itemFlashSaleQuantity <= 0) {
            setIsFlashSaleActive(false);
            setTimeLeft(null);
            return;
        }

        if (!flashSaleWindow.start || !flashSaleWindow.end) {
            setIsFlashSaleActive(false);
            setTimeLeft(null);
            return;
        }

        const updateTimer = () => {
            const now = new Date().getTime();
            if (now >= flashSaleWindow.start && now <= flashSaleWindow.end) {
                setIsFlashSaleActive(true);
                const distance = flashSaleWindow.end - now;
                setTimeLeft({
                    hours: String(Math.floor(distance / (1000 * 60 * 60))).padStart(2, '0'),
                    minutes: String(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0'),
                    seconds: String(Math.floor((distance % (1000 * 60)) / 1000)).padStart(2, '0')
                });
            } else {
                setIsFlashSaleActive(false);
                setTimeLeft(null);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [product, flashSaleWindow]);

    const newsScrollRef = useRef(null); 
    const [isNewsHovered, setIsNewsHovered] = useState(false);

    useEffect(() => {
        // 👉 TỐI ƯU CLS: Ép cuộn trang ngay lập tức (instant) để tránh màn hình bị lưu Layout rác của trang cũ
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        setPurchaseQuantity(1); 
    }, [id]); 

    const scroll = (direction, ref) => {
        const { current } = ref;
        if (current && current.firstElementChild) {
            const itemWidth = current.firstElementChild.getBoundingClientRect().width;
            const gap = window.innerWidth >= 768 ? 16 : 12; 
            const scrollAmount = itemWidth + gap;
            
            if (direction === 'right') {
                const isAtEnd = Math.ceil(current.scrollLeft + current.clientWidth) >= current.scrollWidth - 10;
                if (isAtEnd) {
                    current.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                }
            } else if (direction === 'left') {
                if (current.scrollLeft <= 10) {
                    current.scrollTo({ left: current.scrollWidth, behavior: 'smooth' });
                } else {
                    current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                }
            }
        }
    };

    useEffect(() => {
        if (news.length <= 3 || isNewsHovered) return;
        const timer = setInterval(() => scroll('right', newsScrollRef), 5000);
        return () => clearInterval(timer);
    }, [news.length, isNewsHovered]);

    const handleIncrease = () => {
        if (purchaseQuantity < (product?.itemQuantity || 0)) {
            setPurchaseQuantity(prev => prev + 1);
        } else {
            toast.warn("Đã đạt giới hạn đặt hàng!");
        }
    };

    const handleDecrease = () => {
        if (purchaseQuantity > 1) {
            setPurchaseQuantity(prev => prev - 1);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('access_token'); 
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const rolesObj = payload.scope || payload.roles || payload.authorities || payload.role || '';
                const rolesString = typeof rolesObj === 'string' ? rolesObj : JSON.stringify(rolesObj);

                if (rolesString.includes('ADMIN') || rolesString.includes('STAFF') || rolesString.includes('ROLE_ADMIN') || rolesString.includes('ROLE_STAFF')) {
                    setIsAdminOrStaff(true);
                } 
            } catch (error) { console.error("Lỗi khi giải mã token:", error); }
        }
    }, []);

    useEffect(() => {
        if (product?.itemName) { 
            document.title = `${product.itemName} | Thành Nam PC`; 
            let metaDescriptionTag = document.querySelector('meta[name="description"]');
            if (metaDescriptionTag) {
                const price = product.itemSalePrice || product.itemReleasePrice;
                const priceText = price 
                    ? `với giá ${new Intl.NumberFormat('vi-VN').format(price)}đ` 
                    : '';
                
                const dynamicDescription = `Mua ${product.itemName} chính hãng ${priceText} tại Thành Nam PC. Đặt hàng online tiện lợi, giao hàng tận nhà nhanh chóng tại Quảng Ninh. Dịch vụ sau bán hàng uy tín, chuyên nghiệp.`;
                metaDescriptionTag.setAttribute("content", dynamicDescription);
            }
        }
    }, [product]);

    // 👉 TỐI ƯU 2: Tách cấu trúc luồng Gọi API để tăng tốc độ hiện hình ảnh (LCP)
    useEffect(() => {
        let isMounted = true; 

        const fetchPrimaryData = async () => {
            if (!id) return; 
            setLoading(true);

            setBreadcrumbs([]); 
            setSimilarItems([]);
            setBestSellers([]);
            setPromotions([]);
            setSortedImages([]);

            try {
                // Ưu tiên SỐ 1: Bóc riêng API gọi Sản phẩm ra lấy trước
                const productData = await itemService.getItemDetail(id);
                if (!isMounted) return;

                setProduct(productData);

                let imagesList = productData.images ? [...productData.images] : [];
                if (imagesList.length > 0) {
                    imagesList.sort((a, b) => (a.isMain === b.isMain) ? 0 : a.isMain ? -1 : 1);
                    setSortedImages(imagesList);
                } else {
                    if(productData.itemImage) setSortedImages([{ itemImageUrl: productData.itemImage, isMain: true }]);
                    else setSortedImages([]);
                }
                setActiveImageIndex(0); 

                // TẮT LOADING NGAY LẬP TỨC để Render DOM và Ảnh chính liền
                setLoading(false);

                // Ưu tiên SỐ 2: Gọi các cấu hình phụ trợ sau khi đã tắt Loading
                const [configRes, specsRes, salesRes] = await Promise.all([
                    catalogService.getSystemConfig().catch(() => null),
                    catalogService.getSpecs().catch(() => []),
                    catalogService.getPublicSales().catch(() => [])
                ]);

                if (!isMounted) return;

                const specsData = Array.isArray(specsRes?.data) ? specsRes.data : (Array.isArray(specsRes) ? specsRes : []);
                setAdminSpecs(specsData);

                const actualConfig = configRes?.data || configRes; 
                if (actualConfig) {
                    if (actualConfig.flash_sale_start && actualConfig.flash_sale_end) {
                        setFlashSaleWindow({
                            start: new Date(actualConfig.flash_sale_start).getTime(),
                            end: new Date(actualConfig.flash_sale_end).getTime()
                        });
                    }
                    if (actualConfig.rental_note) setRentalNote(actualConfig.rental_note);
                    if (actualConfig.product_policies) {
                        try { setProductPolicies(JSON.parse(actualConfig.product_policies)); } 
                        catch (e) { console.error("Lỗi parse policy", e); }
                    }
                }

                const allSales = Array.isArray(salesRes?.data) ? salesRes.data : (Array.isArray(salesRes) ? salesRes : []);
                if (productData.saleIds && productData.saleIds.length > 0) {
                    const productPromos = allSales.filter(s => productData.saleIds.includes(s.saleId));
                    setPromotions(productPromos);
                }

                // Chạy background fetch dữ liệu thứ cấp (Similar, Best Sellers, News, Breadcrumbs)
                fetchSecondaryData(productData, actualConfig);

            } catch (error) {
                console.error("Lỗi tải chi tiết SP:", error);
                toast("Không tìm thấy sản phẩm!");
                navigate('/'); 
            } finally { 
                if (isMounted) setLoading(false); 
            }
        };

        const fetchSecondaryData = async (productData, actualConfig) => {
            try {
                const targetCategoryId = productData.categoryId || productData.deviceCategory?.deviceCategoryId;

                const [menusRes, bestSellersRes, newsRes] = await Promise.all([
                    catalogService.getPublicMenus().catch(() => []),
                    itemService.getBestSellers().catch(() => []),
                    newsService.getPublicNews().catch(() => [])
                ]);

                if (!isMounted) return;

                const allMenus = Array.isArray(menusRes?.data) ? menusRes.data : (Array.isArray(menusRes) ? menusRes : []);

                let targetMenuIds = [];
                if (productData.purposeIds && productData.purposeIds.length > 0) {
                    const currentPurposeIds = productData.purposeIds.map(String);
                    const findLeafMenusWithPurposes = (menuList) => {
                        for (const m of menuList) {
                            const hasMatchingPurpose = currentPurposeIds.some(pId => m.menuUrl && m.menuUrl.includes(`purpose=${pId}`));
                            const isLeaf = !m.subMenus || m.subMenus.length === 0;
                            if (hasMatchingPurpose && isLeaf) targetMenuIds.push(m.menuId);
                            if (m.subMenus && m.subMenus.length > 0) findLeafMenusWithPurposes(m.subMenus);
                        }
                    };
                    findLeafMenusWithPurposes(allMenus);
                }
                
                targetMenuIds = [...new Set(targetMenuIds)];

                let similarItemsRaw = [];
                if (targetMenuIds.length > 0) {
                    const responses = await Promise.all(targetMenuIds.map(menuId => itemService.getItemsByMenuId(menuId).catch(() => [])));
                    responses.forEach(res => {
                        const items = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                        similarItemsRaw = [...similarItemsRaw, ...items];
                    });
                    const uniqueMap = new Map();
                    similarItemsRaw.forEach(item => {
                        if (!uniqueMap.has(item.itemId)) uniqueMap.set(item.itemId, item);
                    });
                    similarItemsRaw = Array.from(uniqueMap.values());
                } else {
                    const res = targetCategoryId ? await itemService.getItemsByCategoryId(targetCategoryId).catch(() => []) : [];
                    similarItemsRaw = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                }

                setTimeout(() => {
                    let crumbs = [{ name: 'Trang chủ', link: '/' }];
                    let foundMenuPath = null;

                    const searchMenuPathByCondition = (menuList, currentPath, conditionFn) => {
                        for (const m of menuList) {
                            const linkUrl = `/danh-muc/${generateSlug(m.menuName || 'danh-muc')}-id${m.menuId}`;
                            const pathWithMe = [...currentPath, { name: m.menuName, link: linkUrl }];
                            if (conditionFn(m)) return pathWithMe;
                            if (m.subMenus && m.subMenus.length > 0) {
                                const result = searchMenuPathByCondition(m.subMenus, pathWithMe, conditionFn);
                                if (result) return result;
                            }
                        }
                        return null;
                    };

                    if (productData.purposeIds && productData.purposeIds.length > 0) {
                        const targetPurposeId = productData.purposeIds[0];
                        const regex = new RegExp(`[?&]purpose=${targetPurposeId}(&|$)`);
                        foundMenuPath = searchMenuPathByCondition(allMenus, [], (m) => regex.test(m.menuUrl || ''));
                    }

                    if (foundMenuPath) {
                        crumbs = [...crumbs, ...foundMenuPath];
                    } else {
                        if (productData.deviceCategory) {
                            crumbs.push({ 
                                name: productData.deviceCategory.deviceCategoryName, 
                                link: `/danh-muc/${generateSlug(productData.deviceCategory.deviceCategoryName || 'danh-muc')}-id${targetCategoryId}` 
                            });
                        }
                        if (productData.purposeNames && productData.purposeNames.length > 0) {
                            crumbs.push({ 
                                name: productData.purposeNames[0], 
                                link: `/danh-muc/${generateSlug(productData.deviceCategory?.deviceCategoryName || 'danh-muc')}-id${targetCategoryId}?purpose=${productData.purposeIds[0]}` 
                            });
                        }
                    }
                    crumbs.push({ name: productData.itemName, link: null });
                    setBreadcrumbs(crumbs);

                    const rawBS = Array.isArray(bestSellersRes?.data) ? bestSellersRes.data : (Array.isArray(bestSellersRes) ? bestSellersRes : []);
                    const bestsellerLimit = parseInt(actualConfig?.product_detail_bestseller_limit) || 10;
                    setBestSellers(rawBS.filter(item => String(item.itemId) !== String(productData.itemId)).slice(0, bestsellerLimit));

                    const ALLOWED_STATUSES = [1, 2, 3];
                    let relatedItems = similarItemsRaw.filter(item => {
                        const isNotSameItem = String(item.itemId) !== String(productData.itemId);
                        const isValidStatus = ALLOWED_STATUSES.includes(Number(item.itemStatus));
                        return isNotSameItem && isValidStatus;
                    });

                    relatedItems.sort((a, b) => {
                        if (a.itemStatus === 2 && b.itemStatus !== 2) return -1;
                        if (a.itemStatus !== 2 && b.itemStatus === 2) return 1;
                        return (b.priority || 0) - (a.priority || 0) || b.itemId - a.itemId;
                    });

                    const similarLimit = parseInt(actualConfig?.product_detail_similar_limit) || 10;
                    setSimilarItems(relatedItems.slice(0, similarLimit));

                    const rawNews = Array.isArray(newsRes?.data) ? newsRes.data : (Array.isArray(newsRes) ? newsRes : []);
                    const featuredNews = rawNews.filter(article => article.isFeatured === true);

                    if (featuredNews.length > 0) {
                        setNews(featuredNews.slice(0, 8));
                    } else {
                        setNews(rawNews.slice(0, 8));
                    }
                }, 50);

            } catch (e) {
                console.error("Lỗi khi kéo dữ liệu nền:", e);
            }
        };

        fetchPrimaryData();

        return () => { isMounted = false; };
    }, [id, navigate]);

    const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price || 0);

    const handleAddToCart = async () => { 
        try {
            await cartService.addToCart(product.itemId, purchaseQuantity); 
            toast.success(`Đã thêm ${purchaseQuantity} sản phẩm vào giỏ hàng! 🛒`);
            fetchCart();
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                toast.error("Vui lòng đăng nhập để thêm vào giỏ hàng!");
                navigate('/login');
            } else { toast.error(error.response?.data || "Lỗi khi thêm vào giỏ hàng!"); }
        }
    };

    const handleBuyNow = async () => {
        try {
            await cartService.addToCart(product.itemId, purchaseQuantity);
            await fetchCart(); 
            navigate('/cart', { state: { buyNowItemId: product.itemId } });
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                toast.error("Vui lòng đăng nhập để mua hàng!");
                navigate('/login');
            }
        }
    };

    const handlePrevImage = useCallback((e) => { 
        if (e) e.stopPropagation(); 
        setActiveImageIndex((prev) => (prev === 0 ? sortedImages.length - 1 : prev - 1)); 
    }, [sortedImages.length]);

    const handleNextImage = useCallback((e) => { 
        if (e) e.stopPropagation();
        setActiveImageIndex((prev) => (prev === sortedImages.length - 1 ? 0 : prev + 1)); 
    }, [sortedImages.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isLightboxOpen) return;
            if (e.key === 'Escape') setIsLightboxOpen(false);
            if (e.key === 'ArrowLeft') handlePrevImage();
            if (e.key === 'ArrowRight') handleNextImage();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen, handlePrevImage, handleNextImage]); 

    const { validSpecs, importantSpecNames } = React.useMemo(() => {
        if (!product || !product.itemSpecs || !adminSpecs) return { validSpecs: [], importantSpecNames: [] };

        const currentCategorySpecs = adminSpecs.filter(adminSpec => String(adminSpec.deviceCategory?.deviceCategoryId) === String(product.categoryId));
        const importantNames = currentCategorySpecs.filter(adminSpec => adminSpec.isFilterable === true).map(adminSpec => adminSpec.specName?.trim().toLowerCase());

        const specOrderMap = {};
        currentCategorySpecs.forEach(adminSpec => {
            if (adminSpec.specName) specOrderMap[adminSpec.specName.trim().toLowerCase()] = adminSpec.specOrder ?? 9999;
        });

        const stripHtml = (html) => {
            if (!html) return '';
            return html.replace(/<\/(p|div|li|h[1-6])>/gi, ' ').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        };

        const filtered = product.itemSpecs.filter(spec => {
            const rawValue = spec.spec?.specName?.trim() || "";
            const textOnly = stripHtml(spec.specValue || "").trim();
            const isImportant = importantNames.includes(rawValue.toLowerCase());
            return isImportant || textOnly !== '';
        });

        const sorted = filtered.sort((a, b) => {
            const nameA = a.specName?.trim().toLowerCase();
            const nameB = b.specName?.trim().toLowerCase();
            const orderA = specOrderMap[nameA] ?? 9999;
            const orderB = specOrderMap[nameB] ?? 9999;
            return orderA - orderB; 
        });

        return { validSpecs: sorted, importantSpecNames: importantNames };
    }, [product, adminSpecs]);

    const hasMoreSpecs = validSpecs.length > 4;

    // 👉 TỐI ƯU 3: Thay thế cục Spinner bằng SKELETON UI chuẩn Mobile/Desktop
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-100 flex flex-col font-inter">
                <Header />
                <div className="bg-gradient-to-r from-orange-100 via-orange-50 to-white border-b border-orange-200/60 sticky top-0 z-30 shadow-sm min-h-[48px] md:min-h-[52px] flex items-center">
                    <div className="container mx-auto px-4 flex gap-2">
                        <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                </div>
                <main className="container mx-auto px-4 pt-6 pb-8 flex-grow">
                    <div className="grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-6 mb-8">
                        <div className="bg-orange-100 rounded-3xl shadow-xl p-4 flex flex-col gap-4">
                            <div className="bg-white rounded-2xl w-full aspect-square flex items-center justify-center animate-pulse">
                                <div className="w-24 h-24 text-gray-200">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                                </div>
                            </div>
                            <div className="flex gap-2 min-h-[88px]">
                                {[1, 2, 3, 4].map(i => <div key={i} className="w-20 h-20 bg-gray-200 rounded-xl animate-pulse"></div>)}
                            </div>
                        </div>
                        <div className="bg-white rounded-3xl shadow-xl p-6 flex flex-col gap-4">
                            <div className="w-full h-8 bg-gray-200 rounded-lg animate-pulse mb-2"></div>
                            <div className="w-3/4 h-8 bg-gray-200 rounded-lg animate-pulse mb-4"></div>
                            <div className="w-1/2 h-5 bg-gray-100 rounded animate-pulse mb-6"></div>
                            <div className="w-full h-24 bg-gray-100 rounded-xl animate-pulse mb-6"></div>
                            <div className="w-full h-12 bg-gray-100 rounded-xl animate-pulse mb-4 mt-auto"></div>
                            <div className="flex gap-3">
                                <div className="flex-1 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                                <div className="flex-1 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (!product) return null;

    const isContactPrice = !product.itemReleasePrice || product.itemReleasePrice <= 0;
    const isFlashSale = !isContactPrice && product.itemFlashSalePrice && product.itemFlashSalePrice > 0 && product.itemFlashSaleQuantity > 0 && isFlashSaleActive;
    const currentPrice = isFlashSale ? product.itemFlashSalePrice : (product.itemSalePrice || product.itemReleasePrice);
    const originalPrice = product.itemReleasePrice;
    const isNormalSale = !isContactPrice && !isFlashSale && product.itemSalePrice && product.itemSalePrice < originalPrice;

    const statusConfig = PRODUCT_STATUS_CONFIG[product.itemStatus] || { 
        text: "Đang cập nhật", color: "text-gray-500", bg: "bg-gray-100", Icon: LucideIcons.HelpCircle 
    };

    const displayText = product.itemStatus === 3 ? "Đặt hàng" : statusConfig.text;
    
    const hasDescription = product.description && (
        product.description.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim() !== '' || 
        /<img|<iframe|<video/i.test(product.description)
    );

    const hasGeneralConfig = product.generalConfig && (
        product.generalConfig.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim() !== '' || 
        /<img|<iframe|<video/i.test(product.generalConfig)
    );

    const hasRightCol = hasGeneralConfig || productPolicies.length > 0 || (!hasGeneralConfig && validSpecs.length > 0);

    const renderSpecsTable = () => (
        <div className="bg-gradient-to-b from-orange-100 to-white rounded-3xl shadow-sm border border-orange-200 p-5 md:p-6 h-fit">
            <h3 className="text-base md:text-lg font-extrabold text-gray-800 mb-4 flex items-center gap-2">
                <Settings size={20} className="text-tnc-orange" /> Thông số chi tiết
            </h3>
            
            <div className="relative max-h-[160px] overflow-hidden rounded-xl border border-orange-200/60 bg-white/40 backdrop-blur-sm shadow-inner">
                <table className="w-full table-fixed text-xs md:text-[13px] text-left">
                    <colgroup>
                        <col className="w-[35%] md:w-[30%]" />
                        <col className="w-[65%] md:w-[70%]" />
                    </colgroup>
                    <tbody>
                        {validSpecs.map((spec, idx) => {
                            const currentSpecName = spec.spec?.specName || "N/A";
                            const isImportant = importantSpecNames.includes(currentSpecName.trim().toLowerCase());
                            
                            return (
                                <tr key={idx} className={`border-b border-orange-100/50 transition-colors hover:bg-orange-100/40 ${idx % 2 === 0 ? 'bg-orange-50/30' : 'bg-transparent'}`}>
                                    {isImportant ? (
                                        <td colSpan="2" className="py-3 px-4 font-extrabold text-tnc-orange uppercase tracking-wider text-[11px] md:text-xs bg-orange-100/50 border-y border-orange-200/50">
                                            {currentSpecName}
                                        </td>
                                    ) : (
                                        <>
                                            <td className="py-2.5 px-3 md:px-4 font-bold text-gray-600 w-[35%] md:w-[30%] border-r border-orange-100/50 align-top break-words">
                                                {currentSpecName}
                                            </td>
                                            <td className="py-2.5 px-3 md:px-4 text-gray-800 font-medium w-[65%] md:w-[70%] break-words align-top">
                                                <SafeHtml content={spec.specValue} className="html-content [&_*]:!bg-transparent [&_p]:m-0 [&_ul]:my-0 [&_ul]:pl-4 [&_ol]:pl-4" />
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {hasMoreSpecs && <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none"></div>}
            </div>

            {hasMoreSpecs && (
                <button 
                    onClick={() => setIsSpecModalOpen(true)} 
                    className="w-full mt-5 py-2.5 text-sm text-tnc-orange font-bold border-2 border-orange-200 bg-white rounded-xl hover:bg-tnc-orange hover:border-tnc-orange hover:text-white transition-all duration-300 shadow-sm active:scale-[0.98]"
                >
                    Xem đầy đủ thông số kỹ thuật
                </button>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col relative font-inter">
            <Header />
            
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes gradient-x { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
                .animate-gradient-x { background-size: 200% 200%; animation: gradient-x 3s ease infinite; }
            `}} />

            <div className="bg-gradient-to-r from-orange-100 via-orange-50 to-white border-b border-orange-200/60 sticky top-0 z-30 shadow-sm">
                <div className="container mx-auto px-4 py-3.5 text-[13px] md:text-sm flex items-center gap-1.5 md:gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide min-h-[48px] md:min-h-[52px]">
                    {breadcrumbs.map((crumb, idx) => (
                        <React.Fragment key={idx}>
                            {crumb.link ? (
                                <Link to={crumb.link} className="text-gray-600 font-bold hover:text-tnc-orange transition-colors">
                                    {crumb.name}
                                </Link>
                            ) : (
                                <span className="text-gray-900 font-extrabold truncate">{crumb.name}</span>
                            )}
                            {idx < breadcrumbs.length - 1 && <ChevronRight size={14} className="flex-shrink-0 text-gray-400" />}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <main className="container mx-auto px-4 pt-6 pb-8 flex-grow">
                <div className={`grid grid-cols-1 gap-6 mb-8 ${hasRightCol ? 'lg:grid-cols-[8fr_8fr_8fr]' : 'lg:grid-cols-[5fr_7fr]'}`}>
                    
                    <div className="flex flex-col gap-6 min-w-0">
                        <div className="bg-orange-100 rounded-3xl shadow-xl shadow-orange-200/30 border border-orange-300/90 p-4 flex flex-col gap-4">
                            <div 
                                className="bg-white relative rounded-2xl overflow-hidden p-1 flex items-center justify-center aspect-square group cursor-pointer"
                                onClick={() => { if (sortedImages.length > 0) setIsLightboxOpen(true); }}
                            >
                                {product.brand?.brandImageUrl && (
                                    <div className="absolute top-3 left-3 z-20 pointer-events-none opacity-80 mix-blend-multiply w-16 h-10 flex items-center justify-start">
                                        <img 
                                            src={getImageUrl(product.brand.brandImageUrl)} 
                                            alt={product.brand.brandName} 
                                            width="64" height="40"
                                            className="max-w-full max-h-full object-contain drop-shadow-sm" 
                                        />
                                    </div>
                                )}

                                <div className="absolute bottom-3 left-3 z-20 pointer-events-none w-24 h-10 flex items-end">
                                    <img 
                                        src={WebLogo} 
                                        alt="Watermark" 
                                        width="96" height="40"
                                        className="max-h-full w-auto drop-shadow-md object-left object-contain" 
                                    />
                                </div>
                                {sortedImages.length > 0 ? (
                                    <>
                                        {/* 👉 TỐI ƯU 4: Sửa decoding="sync" thành "async" để mở luồng Main Thread (Cải thiện LCP Desktop/Mobile) */}
                                        <img 
                                            src={getImageUrl(sortedImages[activeImageIndex].itemImageUrl)} 
                                            alt={product.itemName || "Sản phẩm"} 
                                            className="w-full h-full object-contain transition-all duration-300 mix-blend-multiply aspect-square" 
                                            fetchPriority="high"
                                            decoding="async"
                                            loading="eager"
                                        />
                                        <div className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm p-1.5 rounded-lg text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Maximize2 size={20} />
                                        </div>
                                    </>
                                ) : (
                                    <img src={getImageUrl(null)} alt="Chưa cập nhật" className="w-full h-full object-contain mix-blend-multiply aspect-square" />
                                )}

                                {sortedImages.length > 1 && (
                                    <>
                                        <button onClick={handlePrevImage} className="absolute left-2 bg-white/80 p-2 rounded-full shadow hover:bg-white text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity z-10"><ChevronLeft size={24} /></button>
                                        <button onClick={handleNextImage} className="absolute right-2 bg-white/80 p-2 rounded-full shadow hover:bg-white text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity z-10"><ChevronRight size={24} /></button>
                                    </>
                                )}
                            </div>
                            
                            {sortedImages.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide min-h-[88px]">
                                    {sortedImages.map((img, idx) => (
                                        <div key={idx} onClick={() => setActiveImageIndex(idx)} className={`w-20 h-20 flex-shrink-0 border-2 rounded-xl cursor-pointer p-1 overflow-hidden transition-all duration-300 bg-gray-50 ${activeImageIndex === idx ? 'border-tnc-orange opacity-100' : 'border-transparent opacity-60 hover:opacity-100 hover:border-gray-300'}`}>
                                            <img src={getImageUrl(img.itemImageUrl)} alt={`Góc nhìn ${idx}`} width="80" height="80" className="w-full h-full object-cover rounded mix-blend-multiply" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {hasGeneralConfig && validSpecs.length > 0 && renderSpecsTable()}
                    </div>

                    <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 p-6 flex flex-col min-w-0 h-fit">
                        <div className="flex items-start justify-between gap-4 mb-2">
                            {/* 👉 TỐI ƯU 5: Vá lỗi hiển thị nhầm biến 'news.newsTitle' thành 'product.itemName' ở Title gây crash/CLS */}
                            <h1 className="text-lg font-extrabold text-gray-800 leading-snug">{product.itemName}</h1>
                            {isAdminOrStaff && (
                                <Link to={`/admin/products/edit/${product.itemId}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-colors shadow-sm flex-shrink-0" title="Mở trong tab mới để chỉnh sửa"><Edit size={18} /></Link>
                            )}
                        </div>

                        <div className="flex items-center gap-3 text-xs text-gray-800 mb-3 pb-3 border-b border-gray-100 min-h-[30px]">
                            <span>Mã SP: <span className="font-bold text-blue-600">{product.itemCode || product.itemId}</span></span>
                            <span className="text-gray-300">|</span>
                            <span className="flex items-center gap-2">Hãng: {product.brand?.brandImageUrl ? (<img src={getImageUrl(product.brand.brandImageUrl)} alt={product.brand?.brandName || "Logo hãng"} width="40" height="20" className="h-5 w-auto object-contain" title={product.brand?.brandName} />) : (<span className="font-bold text-gray-700">{product.brand?.brandName || 'Đang cập nhật'}</span>)}</span>
                        </div>

                        {!product.isForRent && (
                            <div className="flex items-center gap-2 mb-4">
                                <ShieldCheck size={18} className="text-green-500" />
                                <span className="text-sm font-bold text-green-600 uppercase tracking-wide">Hàng mới chính hãng 100%</span>
                            </div>
                        )}

                        {product.isForRent ? (
                            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 mb-5 shadow-sm min-h-[150px]">
                                <h3 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2">
                                    <Calendar size={20} className="text-orange-600"/> Bảng Giá Cho Thuê
                                </h3>
                                <div className="space-y-2.5">
                                    {product.rentalPrices && product.rentalPrices.length > 0 ? (
                                        product.rentalPrices.map((tier, idx) => (
                                            <div key={idx} className="flex justify-between items-center bg-white p-3.5 rounded-lg border border-orange-100 shadow-sm hover:border-orange-300 transition-colors">
                                                <span className="font-bold text-gray-700">{tier.durationLabel || 'Chưa cập nhật'}</span>
                                                <span className={`font-extrabold text-lg ${tier.price && tier.price > 0 ? 'text-tnc-orange' : 'text-blue-600'}`}>
                                                    {tier.price && tier.price > 0 ? formatPrice(tier.price) + '/sp' : 'Liên hệ'}
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 bg-white rounded-lg border border-orange-100 text-gray-500 font-medium">Vui lòng liên hệ để nhận báo giá thuê chi tiết.</div>
                                    )}
                                </div>
                                
                                {product.rentalPrices && product.rentalPrices.some(r => r.price && r.price > 0) && (<div className="text-[11px] mt-2 font-medium text-orange-700/70 italic text-right">*Giá thuê đã bao gồm VAT</div>)}
                                {rentalNote && (
                                    <div className="mt-3 bg-orange-100/50 p-3 rounded-xl border border-orange-200/50">
                                        <div className="text-xs font-bold text-orange-800 mb-1">📌 Ghi chú thuê sản phẩm:</div>
                                        <SafeHtml content={rentalNote} className="text-xs text-orange-900/90 leading-relaxed html-content [&_*]:!bg-transparent [&_p]:mb-1 [&_ul]:my-1 [&_ul]:pl-4 [&_ul]:list-disc [&_strong]:font-bold" />
                                    </div>
                                )}
                                {promotions && promotions.length > 0 && !product.isForRent && !isContactPrice && !isFlashSale && (<span className={`text-[11px] mt-0.5 font-medium italic ${isFlashSale ? 'text-white/80' : 'text-tnc-orange'}`}>*Giá chưa bao gồm ưu đãi kèm theo</span>)}

                                <div className="mt-4 pt-3 border-t border-orange-200/60 text-xs text-orange-700/80 font-medium flex items-start gap-1.5">
                                    <ShieldCheck size={14} className="shrink-0 mt-0.5" />
                                    <span>Tất cả thiết bị đều được bảo dưỡng kỹ lưỡng. Thủ tục thuê nhanh gọn, hỗ trợ kỹ thuật tận tình.</span>
                                </div>
                            </div>
                        ) : (
                            <div className={`rounded-xl p-4 mb-5 border-2 shadow-md flex flex-col justify-center transition-all min-h-[110px] ${
                                isContactPrice ? 'bg-gradient-to-r from-orange-100 via-orange-50 to-white border-tnc-orange shadow-sm' : isFlashSale ? 'animate-gradient-x bg-gradient-to-r from-tnc-orange-hover via-tnc-orange to-orange-500 border-tnc-orange text-white shadow-orange-200' : 'bg-gradient-to-r from-orange-100 via-orange-50 to-white border-tnc-orange shadow-sm'
                            }`}>
                                {isContactPrice ? (
                                    <div className="flex flex-col py-2"><div className="text-2xl font-black text-blue-900 tracking-tight drop-shadow-sm">Giá Liên Hệ</div></div>
                                ) : (
                                    <>
                                        {isFlashSale && (
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 pb-3 border-b border-white/20 min-h-[32px]">
                                                <div className="flex items-center gap-1.5 text-sm font-bold uppercase text-yellow-300"><Zap fill="currentColor" size={18}/> GIỜ VÀNG DEAL SỐC</div>
                                                {timeLeft && (
                                                    <div className="flex items-center gap-1.5 font-mono text-sm">
                                                        <span className="text-white/80 text-xs font-sans mr-1">Kết thúc trong:</span>
                                                        <span className="bg-white text-tnc-orange px-2 py-1 rounded shadow-sm font-bold w-7 text-center">{timeLeft.hours}</span><span className="text-white font-bold">:</span>
                                                        <span className="bg-white text-tnc-orange px-2 py-1 rounded shadow-sm font-bold w-7 text-center">{timeLeft.minutes}</span><span className="text-white font-bold">:</span>
                                                        <span className="bg-white text-tnc-orange px-2 py-1 rounded shadow-sm font-bold w-7 text-center">{timeLeft.seconds}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex flex-col">
                                            <span className={`text-xs font-bold ${isFlashSale ? 'text-white/90' : 'text-gray-700'}`}>{isFlashSale ? 'Giá Flash Sale:' : (isNormalSale ? 'Giá Khuyến mãi:' : 'Giá Bán lẻ:')}</span>
                                            <div className="flex items-baseline gap-2.5 mt-1">
                                                <span className={`text-2xl font-black tracking-tight ${isFlashSale ? 'text-white' : 'text-tnc-orange'}`}>{formatPrice(currentPrice)}</span>
                                                {(isFlashSale || isNormalSale) && originalPrice > currentPrice && (<span className={`text-sm font-medium line-through ${isFlashSale ? 'text-white/70' : 'text-gray-400'}`}>{formatPrice(originalPrice)}</span>)}
                                            </div>
                                            <span className={`text-[11px] mt-1 font-medium ${isFlashSale ? 'text-white/80' : 'text-gray-500'}`}>*Giá đã bao gồm VAT</span>
                                            {promotions && promotions.length > 0 && !product.isForRent && !isContactPrice && !isFlashSale && (<span className={`text-[11px] mt-0.5 font-medium ${isFlashSale ? 'text-white/80' : 'text-gray-500'}`}>*Giá chưa bao gồm ưu đãi mua kèm</span>)}
                                        </div>
                                        {isFlashSale && (<div className="mt-2 pt-2 border-t border-white/20 text-xs font-medium text-white/90"><span>Mua tối đa <strong>{product.itemFlashSaleQuantity}</strong> sản phẩm</span></div>)}
                                    </>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col gap-3 mb-5 border-b border-gray-100 pb-5 min-h-[60px]">
                            <div className="flex items-center gap-1.5 text-sm">
                                {statusConfig.Icon && <statusConfig.Icon size={16} className={statusConfig.color} />}
                                <span className="text-gray-800 font-medium">Tình trạng:</span>
                                <span className={`ml-1 font-bold ${statusConfig.color}`}>{displayText}</span>
                            </div>
                            {product.warrantyTime && (
                                <div className="flex items-start gap-2 text-sm">
                                    <ShieldCheck size={16} className="text-green-600 shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0 leading-snug">
                                        <span className="text-gray-800 font-medium whitespace-nowrap mr-1.5">Bảo hành chính hãng:</span>
                                        <span className="font-bold text-green-600 break-words">{product.warrantyTime}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {!product.isForRent && !isContactPrice && !isFlashSale && promotions.length > 0 && (
                            <div className="mb-6 bg-white rounded-2xl shadow-sm border border-orange-200/60 overflow-hidden hover:shadow-md hover:shadow-orange-100/50 transition-all duration-300 group">
                                <h3 className="text-[14px] md:text-[15px] font-extrabold text-white bg-gradient-to-r from-tnc-orange-dark via-tnc-orange to-tnc-orange-light px-4 md:px-5 py-2 md:py-2.5 flex items-center gap-3 m-0">
                                    <div className="p-1.5 bg-white/20 text-white rounded-lg transition-colors shrink-0"><Gift size={18} /></div>
                                    <span className="truncate leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] uppercase">Ưu đãi mua kèm</span>
                                </h3>
                                <div className="px-4 py-3 md:px-5 md:py-2.5 bg-gradient-to-r from-orange-50 to-white space-y-3">
                                    {promotions.map((promo, idx) => (
                                        <div key={promo.saleId} className="flex gap-2.5 text-xs items-start">
                                            <span className="flex-shrink-0 w-4 h-4 bg-tnc-orange text-white rounded-full flex items-center justify-center font-bold mt-0.5" style={{fontSize: '10px'}}>{idx + 1}</span>
                                            <div className="text-gray-800 leading-snug flex-1 w-full min-w-0">
                                                {promo.saleName && <div className="font-bold text-tnc-orange mb-1 text-sm truncate">{promo.saleName}</div>}
                                                <SafeHtml content={promo.description} className="text-[13px] md:text-[13.5px] text-gray-800 font-normal pl-1.5 break-words [&_*]:!bg-transparent [&_img]:max-w-full [&_img]:h-auto [&_img]:min-h-[100px] [&_img]:object-contain [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_li]:mb-0.5 [&_p]:my-0.5 [&_strong]:font-bold [&_em]:italic" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!product.isForRent && (
                            <div className="flex flex-col gap-3 mb-6">
                                <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-blue-50 to-white border border-blue-100 rounded-xl shadow-sm">
                                    <CreditCard size={18} className="text-blue-600 shrink-0" />
                                    <span className="text-[13.5px] text-blue-900"><span className="font-extrabold text-blue-700">Mua trả góp:</span> Liên hệ để biết thêm chi tiết</span>
                                </div>
                                <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-emerald-50 to-white border border-emerald-100 rounded-xl shadow-sm">
                                    <LucideIcons.Wallet size={18} className="text-emerald-600 shrink-0" />
                                    <span className="text-[13.5px] text-emerald-900"><span className="font-extrabold text-emerald-700">Thanh toán:</span> Tiền mặt, Chuyển khoản (QR)</span>
                                </div>
                            </div>
                        )}

                        <div className="pt-2 mt-auto min-h-[50px]">
                            {product.isForRent ? (
                                <a href="tel:0912226390" className="block w-full bg-tnc-orange text-white font-bold py-4 px-6 rounded-xl hover:bg-tnc-orange-hover transition-colors text-center shadow-lg shadow-orange-200 uppercase tracking-wide flex items-center justify-center gap-3"><PhoneCall size={22} className="animate-pulse" /> Liên hệ thuê sản phẩm</a>
                            ) : isContactPrice ? (
                                <a href="tel:0912226390" className="block w-full bg-blue-800 text-white font-bold py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors text-center shadow-lg shadow-blue-200 uppercase tracking-wide flex items-center justify-center gap-3"><PhoneCall size={22} className="animate-pulse text-white" /> Liên hệ nhận báo giá</a>
                            ) : [PRODUCT_STATUS.HIDDEN, PRODUCT_STATUS.COMING_SOON, PRODUCT_STATUS.OUT_OF_STOCK, PRODUCT_STATUS.DISCONTINUED].includes(product.itemStatus) ? (
                                <div className={`border font-bold py-4 px-6 rounded-xl text-center shadow-lg uppercase tracking-wide text-white flex items-center justify-center gap-2 ${product.itemStatus === PRODUCT_STATUS.COMING_SOON ? 'bg-gradient-to-r from-blue-600 to-cyan-500 border-blue-400/50 shadow-blue-500/40' : product.itemStatus === PRODUCT_STATUS.OUT_OF_STOCK ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-orange-400/50 shadow-orange-500/40' : 'bg-gradient-to-r from-slate-600 to-gray-500 border-slate-400/50 shadow-slate-500/40'}`}>
                                    {product.itemStatus === PRODUCT_STATUS.COMING_SOON ? (<>🚚 Hàng sắp về</>) : product.itemStatus === PRODUCT_STATUS.OUT_OF_STOCK ? (<>🔔 Sản phẩm tạm hết hàng</>) : (<>🚫 Sản phẩm ngừng kinh doanh</>)}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 min-h-[40px]">
                                        <span className="text-xs font-bold text-gray-600 uppercase">Số lượng:</span>
                                        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm h-10 w-28">
                                            <button onClick={handleDecrease} className="flex-1 hover:bg-gray-100 font-bold border-r border-gray-200 transition-colors h-full">-</button>
                                            <input type="text" readOnly value={purchaseQuantity} className="w-10 text-center font-bold text-gray-800 outline-none text-sm h-full" />
                                            <button onClick={handleIncrease} className="flex-1 hover:bg-gray-100 font-bold border-l border-gray-200 transition-colors h-full">+</button>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 flex-col sm:flex-row">
                                        <button onClick={handleAddToCart} className="flex-1 bg-gradient-to-r from-tnc-orange to-yellow-500 text-white font-extrabold py-3.5 rounded-xl hover:from-tnc-orange-hover hover:to-yellow-600 hover:shadow-lg hover:shadow-orange-200/50 transition-all duration-300 flex justify-center items-center gap-2 uppercase tracking-wide text-sm shadow-md active:scale-95"><ShoppingCart size={18} /> Thêm vào giỏ</button>
                                        <button onClick={handleBuyNow} className={`flex-1 text-white font-extrabold py-3.5 rounded-xl hover:-translate-y-0.5 transition-all duration-300 flex justify-center items-center gap-2 text-sm shadow-md hover:shadow-xl uppercase tracking-wide active:scale-95 bg-gradient-to-r from-[#DC2626] via-[#FF3B30] to-[#ff5951] hover:from-[#b91c1c] hover:to-[#FF3B30] shadow-red-200 hover:shadow-red-400/40`}>
                                            {product.itemStatus === PRODUCT_STATUS.PRE_ORDER ? (<><CalendarDays size={18} /> Đặt hàng</>) : (<><Zap size={18} fill="currentColor" /> Mua ngay</>)}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>

                    {hasRightCol && (
                        <div className="flex flex-col gap-4 min-w-0 h-fit">
                            {hasGeneralConfig && (
                                <div className="bg-white rounded-2xl shadow-sm border border-orange-200/60 overflow-hidden hover:shadow-md hover:shadow-orange-100/50 transition-all duration-300 group min-h-[150px]">
                                    <h3 className="text-[14px] md:text-[15px] font-extrabold text-white bg-gradient-to-r from-tnc-orange-dark via-tnc-orange to-tnc-orange-light px-4 md:px-5 py-2 md:py-2.5 flex items-center gap-3 m-0">
                                        <div className="p-1.5 bg-white/20 text-white rounded-lg transition-colors shrink-0"><Settings size={18} /></div>
                                        <span className="truncate leading-snug">Cấu hình chung</span>
                                    </h3>
                                    <div className="px-4 py-3 md:px-5 md:py-2.5 bg-gradient-to-r from-orange-50 to-white">
                                        <SafeHtml content={product.generalConfig} className="html-content text-[13px] md:text-[13.5px] text-gray-800 font-normal leading-relaxed text-left break-words [&_*]:!bg-transparent [&_*]:max-w-full [&_*]:break-words [&_*]:whitespace-pre-wrap [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:mb-1.5 [&_a]:text-tnc-orange [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:min-h-[150px] [&_img]:aspect-video [&_img]:bg-gray-50 [&_img]:object-contain [&_strong]:font-bold [&_b]:font-bold" />
                                    </div>
                                </div>
                            )}

                            {!hasGeneralConfig && validSpecs.length > 0 && renderSpecsTable()}

                            {productPolicies.map((policy, idx) => {
                                const IconComponent = LucideIcons[policy.icon] || LucideIcons.ShieldCheck;
                                return (
                                    <div key={policy.id || idx} className="bg-white rounded-2xl shadow-sm border border-orange-200/60 overflow-hidden hover:shadow-md hover:shadow-orange-100/50 transition-all duration-300 group min-h-[100px]">
                                        <h3 className="text-[14px] md:text-[15px] font-extrabold text-white bg-gradient-to-r from-tnc-orange-dark via-tnc-orange to-tnc-orange-light px-4 md:px-5 py-2 md:py-2.5 flex items-center gap-3 m-0">
                                            <div className="p-1.5 bg-white/20 text-white rounded-lg transition-colors shrink-0"><IconComponent size={18} /></div>
                                            <span className="truncate leading-snug">{policy.title}</span>
                                        </h3>
                                        <div className="px-4 py-3 md:px-5 md:py-1.5 bg-gradient-to-r from-orange-50 to-white">
                                            <SafeHtml content={policy.content} className="html-content text-[13px] md:text-[13.5px] text-gray-800 font-normal leading-relaxed text-justify break-words [&_*]:!bg-transparent [&_*]:max-w-full [&_*]:break-words [&_*]:whitespace-pre-wrap [&_a]:![word-break:break-all] [&_img]:max-w-full [&_img]:h-auto [&_img]:min-h-[100px] [&_img]:aspect-video [&_img]:bg-gray-50 [&_img]:object-contain [&_p]:mb-2 [&_ul]:my-2 [&_ul]:list-none [&_ul]:pl-0 [&_ul_li]:relative [&_ul_li]:pl-4 [&_ul_li::before]:content-['•'] [&_ul_li::before]:text-tnc-orange [&_ul_li::before]:absolute [&_ul_li::before]:left-0 [&_ul_li::before]:font-black [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1.5 [&_b]:font-bold [&_strong]:font-bold" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {hasDescription && (
                    <div ref={sectionRef} className="mb-10 bg-gradient-to-r from-orange-100 to-white p-6 md:p-8 rounded-3xl shadow-sm border border-orange-300/60 w-full relative scroll-mt-24 min-h-[300px] [content-visibility:auto]">
                        <h2 className="text-xl font-bold text-blue-900 mb-5 border-b border-gray-100 pb-3 uppercase tracking-wide">Mô tả sản phẩm</h2>
                        <div className={`relative transition-all duration-700 ease-in-out ql-snow ${!isContentLong || isDescriptionExpanded ? 'max-h-[100000px] overflow-visible' : 'max-h-[400px] overflow-hidden [-webkit-mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)] [mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]'}`}>
                            <SafeHtml ref={descriptionRef} content={product.description} className="ql-editor !p-0 html-content text-sm text-gray-700 leading-relaxed pb-4 break-words [&_*]:!bg-transparent [&_*]:max-w-full [&_*]:break-words [&_*]:whitespace-pre-wrap [&_p]:mb-4 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:text-[#002855] [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mb-4 [&_h2]:text-[#002855] [&_h2]:mt-6 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mb-3 [&_h3]:text-[#002855] [&_h3]:mt-4 [&_a]:text-tnc-orange [&_a]:underline [&_img]:rounded-xl [&_img]:mx-auto [&_img]:my-6 [&_img]:max-w-full [&_img]:h-auto [&_img]:min-h-[250px] [&_img]:aspect-video [&_img]:bg-gray-100 [&_img]:object-contain [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:my-6 [&_table]:block [&_table]:overflow-x-auto [&_th]:border [&_th]:border-orange-200/60 [&_th]:bg-orange-100/50 [&_th]:p-2 [&_th]:text-left [&_td]:border [&_td]:border-orange-200/60 [&_td]:p-2 [&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_.ql-align-justify]:text-justify [&_.ql-indent-1]:ml-8 [&_.ql-indent-2]:ml-16 [&_.ql-indent-3]:ml-24 [&_.ql-indent-4]:ml-32" />
                        </div>
                        {isContentLong && (
                            <div className="mt-4 flex justify-center relative z-10">
                                <button onClick={() => { if (isDescriptionExpanded) { sectionRef.current?.scrollIntoView({ behavior: 'smooth' }); } setIsDescriptionExpanded(!isDescriptionExpanded); }} className="flex items-center gap-2 text-tnc-orange font-bold py-2.5 px-6 rounded-full border-2 border-tnc-orange hover:bg-orange-50 hover:shadow-md transition-all bg-white min-w-[200px] justify-center">
                                    {isDescriptionExpanded ? (<>Thu gọn mô tả <LucideIcons.ChevronUp size={18} strokeWidth={3} /></>) : (<>Xem toàn bộ mô tả <LucideIcons.ChevronDown size={18} strokeWidth={3} /></>)}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <Suspense fallback={<div className="animate-pulse h-[400px] w-full bg-gray-200 rounded-2xl mt-8"></div>}>
                    {similarItems.length > 0 && (
                        <div className="min-h-[400px] [content-visibility:auto]"><ProductRow title="Sản phẩm tương tự" items={similarItems} getImageUrl={getImageUrl} adminSpecs={adminSpecs} hideViewAll={true} /></div>
                    )}
                </Suspense>

                {bestSellers.length > 0 && (
                    <div className="min-h-[400px] [content-visibility:auto]"><ProductRow title="Sản phẩm bán chạy" items={bestSellers} isBestSeller={true} getImageUrl={getImageUrl} adminSpecs={adminSpecs} hideViewAll={true} /></div>
                )}

                {news.length > 0 && (
                    <div className="bg-gradient-to-r from-orange-100 to-white rounded-3xl shadow-sm border border-orange-300/60 p-6 md:p-8 mt-8 min-h-[350px] [content-visibility:auto]">
                        <div className="flex items-center justify-between mb-6 border-b border-orange-200/60 pb-4 min-h-[40px]">
                            <div className="flex items-center gap-2"><Newspaper className="text-tnc-orange" size={24} /><h2 className="text-xl font-extrabold text-[#002855] uppercase tracking-wide">Tin tức nổi bật</h2></div>
                            <Link to="/tin-tuc" className="flex items-center gap-1 text-sm font-bold text-gray-500 hover:text-tnc-orange transition-colors">Xem tất cả <ChevronRight size={16} /></Link>
                        </div>
                        
                        <div className="relative group/slider px-0 md:px-12" onMouseEnter={() => setIsNewsHovered(true)} onMouseLeave={() => setIsNewsHovered(false)}>
                            <button onClick={() => scroll('left', newsScrollRef)} className="absolute left-0 md:-left-2 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-full shadow-[0_4px_15px_rgba(0,0,0,0.25)] hover:scale-110 opacity-0 group-hover/slider:opacity-100 transition-all hidden md:flex bg-white border border-gray-200 text-gray-600 hover:text-tnc-orange"><ChevronLeft size={26} className="pr-0.5" /></button>
                            <div ref={newsScrollRef} className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth py-4 -my-4 min-h-[220px]" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                {news.map(article => (
                                    <Link key={article.newsId} to={`/tin-tuc/${generateSlug(article.newsTitle)}-id${article.newsId}`} className="w-[calc(80%-12px)] sm:w-[calc(40%-12px)] md:w-[calc(25%-12px)] snap-start shrink-0 group flex flex-col">
                                        <div className="rounded-xl overflow-hidden aspect-video mb-3 shadow-sm border border-gray-100 relative bg-gray-100">
                                            <img src={getImageUrl(article.newsImageUrl, 'News')} alt={article.newsTitle} loading="lazy" width="400" height="225" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm line-clamp-2 group-hover:text-tnc-orange transition-colors min-h-[40px]">{article.newsTitle || article.title}</h3>
                                        <div className="mt-auto pt-2 text-xs text-gray-400 font-medium">{new Date(article.newsCreatedTime || article.createdAt || Date.now()).toLocaleDateString('vi-VN')}</div>
                                    </Link>
                                ))}
                            </div>
                            <button onClick={() => scroll('right', newsScrollRef)} className="absolute right-0 md:-right-2 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-full shadow-[0_4px_15px_rgba(0,0,0,0.25)] hover:scale-110 opacity-0 group-hover/slider:opacity-100 transition-all hidden md:flex bg-white border border-gray-200 text-gray-600 hover:text-tnc-orange"><ChevronRight size={26} className="pl-0.5" /></button>
                        </div>
                    </div>
                )}

            </main>
            <Footer />

            {isSpecModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIsSpecModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">Thông số kỹ thuật chi tiết</h3>
                            <button onClick={() => setIsSpecModalOpen(false)} className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"><X size={20} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto">
                            <table className="w-full table-fixed text-sm text-left border border-gray-200 rounded-lg">
                                <colgroup><col className="w-[30%] md:w-[35%]" /><col className="w-[70%] md:w-[65%]" /></colgroup>
                                <tbody>
                                    {validSpecs.map((spec, idx) => {
                                        const currentSpecName = spec.spec?.specName || "N/A";
                                        const isImportant = importantSpecNames.includes(currentSpecName.trim().toLowerCase());
                                        return (
                                            <tr key={idx} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-gray-50/80' : 'bg-white'}`}>
                                                {isImportant ? (
                                                    <td colSpan="2" className="py-3 px-4 font-bold text-tnc-orange bg-orange-50/80 uppercase tracking-wide">{currentSpecName}</td>
                                                ) : (
                                                    <>
                                                        <td className="py-3 px-4 font-bold text-gray-700 w-[30%] md:w-[35%] border-r border-gray-200 align-top break-words">{currentSpecName}</td>
                                                        <td className="py-3 px-4 text-gray-800 leading-relaxed w-[70%] md:w-[65%] break-words align-top"><SafeHtml content={spec.specValue} className="html-content [&_*]:!bg-transparent [&_p]:m-0 [&_ul]:my-0 [&_ul]:pl-4 [&_ol]:pl-4" /></td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {isLightboxOpen && sortedImages.length > 0 && (
                <div className="fixed inset-0 z-[500] bg-black/95 flex items-center justify-center backdrop-blur-sm select-none" onClick={() => setIsLightboxOpen(false)}>
                    <button className="absolute top-4 right-4 z-50 text-white/70 hover:text-white bg-black/50 hover:bg-red-500 p-2 rounded-full transition-all" onClick={() => setIsLightboxOpen(false)} title="Đóng (Esc)"><X size={28} /></button>
                    {sortedImages.length > 1 && (<button className="absolute left-4 z-50 text-white/70 hover:text-white bg-black/50 hover:bg-black p-3 rounded-full transition-all" onClick={handlePrevImage}><ChevronLeft size={36} /></button>)}
                    <div className="w-full h-full p-4 md:p-12 flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()} >
                        <img src={getImageUrl(sortedImages[activeImageIndex].itemImageUrl)} alt={`Góc nhìn ${activeImageIndex}`} className="max-w-full max-h-[80vh] object-contain select-none" draggable="false" />
                        <div className="mt-4 text-white/70 font-medium">{activeImageIndex + 1} / {sortedImages.length}</div>
                    </div>
                    {sortedImages.length > 1 && (<button className="absolute right-4 z-50 text-white/70 hover:text-white bg-black/50 hover:bg-black p-3 rounded-full transition-all" onClick={handleNextImage}><ChevronRight size={36} /></button>)}
                </div>
            )}
        </div>
    );
};

export default ProductDetailPage;